import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Radar, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API, errorText } from '../governance_v2/constants';
import { LEVELS, LEVEL_META, STATUS_LABELS } from './constants';
import { SignalCard } from './SignalCard';
import { ProposalFromSignalDialog } from './ProposalFromSignalDialog';
import { KeyKpisDialog } from './KeyKpisDialog';
import './materiality.css';

const PAGE = 20;

const Filter = ({ value, onChange, items, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-44" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const Stat = ({ label, value, cls, testid }) => (
  <div className="rounded-2xl border border-slate-200 px-4 py-3 dark:border-slate-700" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-2xl font-bold tabular-nums ${cls || ''}`}>{value}</p>
  </div>
);

const useRescan = (companyId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await axios.post(`${API}/companies/${companyId}/materiality/scan`)).data,
    onSuccess: (d) => { toast.success(`Análisis listo: ${d.detected} señales (${d.created} nuevas, ${d.reopened} reabiertas, ${d.resolved} resueltas)`); qc.invalidateQueries({ queryKey: ['materiality'] }); },
    onError: (e) => toast.error(errorText(e)),
  });
};

export const MaterialityPanel = ({ companyId, role }) => {
  const isAdmin = role !== 'staff';
  const [kind, setKind] = useState('all');
  const [level, setLevel] = useState('all');
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [proposing, setProposing] = useState(null);
  const [keysOpen, setKeysOpen] = useState(false);
  const rescan = useRescan(companyId);
  const params = { status, ...(kind !== 'all' && { kind }), ...(level !== 'all' && { level }) };
  const { data, isLoading } = useQuery({
    queryKey: ['materiality', 'signals', companyId, params], enabled: !!companyId, refetchInterval: 60000,
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/materiality/signals`, { params })).data,
  });
  const items = useMemo(() => (data?.items || []).filter((s) => !q || `${s.title} ${s.detail}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const summary = useQuery({
    queryKey: ['materiality', 'summary', companyId], enabled: !!companyId, refetchInterval: 60000,
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/materiality/summary`)).data,
  }).data;
  return (
    <div className="space-y-6" data-testid="materiality-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Radar className="h-7 w-7 text-red-500" />
        <div className="mr-auto"><h2 className="text-2xl font-bold text-slate-900 dark:text-white">Alertas y Oportunidades</h2>
          <p className="text-sm text-slate-500">Puntaje determinista 0-100: magnitud 40% · importancia 25% · urgencia 20% · confianza 15%. Se reanaliza cada hora y tras cada ingesta o cambio de proyecto.</p>
          {data?.last_scan && <p className="text-xs text-slate-400" data-testid="mat-last-scan">Último análisis: {new Date(data.last_scan.at).toLocaleString('es')}</p>}
          {isAdmin && data?.last_scan?.suspicious_kpis?.length > 0 && <p className="text-xs text-amber-700" title={data.last_scan.suspicious_kpis.join(', ')} data-testid="mat-suspicious">
            {data.last_scan.suspicious_kpis.length} KPIs omitidos: su benchmark difiere más de 20× del valor real (posible error de unidad).</p>}</div>
        {isAdmin && <Button variant="outline" onClick={() => setKeysOpen(true)} data-testid="mat-keykpis-btn"><Star className="mr-2 h-4 w-4" />KPIs clave</Button>}
        {isAdmin && <Button onClick={() => rescan.mutate()} disabled={rescan.isPending} data-testid="mat-rescan-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${rescan.isPending ? 'animate-spin' : ''}`} />{rescan.isPending ? 'Analizando…' : 'Reanalizar ahora'}</Button>}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {LEVELS.map((lv) => <Stat key={lv} label={`Alertas ${LEVEL_META[lv].label.toLowerCase()}s`} value={summary?.alerts?.[lv] ?? '—'}
          cls={lv === 'critica' ? 'text-red-600' : lv === 'alta' ? 'text-orange-500' : ''} testid={`mat-stat-${lv}`} />)}
        <Stat label="Oportunidades" value={summary?.opportunities ?? '—'} cls="text-emerald-600" testid="mat-stat-opportunities" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Filter value={kind} onChange={(v) => { setKind(v); setLimit(PAGE); }} testid="mat-filter-kind" items={[['all', 'Todo'], ['alert', 'Alertas'], ['opportunity', 'Oportunidades']]} />
        <Filter value={level} onChange={(v) => { setLevel(v); setLimit(PAGE); }} testid="mat-filter-level" items={[['all', 'Todos los niveles'], ...LEVELS.map((l) => [l, LEVEL_META[l].label])]} />
        <Filter value={status} onChange={(v) => { setStatus(v); setLimit(PAGE); }} testid="mat-filter-status" items={[['active', 'Activas'], ...Object.entries(STATUS_LABELS), ['all', 'Todas']]} />
        <Input className="w-64" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mat-search" />
      </div>
      {isLoading ? <p className="text-sm text-slate-400">Cargando señales…</p> : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="mat-empty">Sin señales con estos filtros.</p>
      ) : (
        <div className="space-y-3" data-testid="mat-list">
          {items.slice(0, limit).map((s, i) => <SignalCard key={s.id} s={s} index={i} companyId={companyId} isAdmin={isAdmin} onPropose={setProposing} />)}
          {items.length > limit && <Button variant="ghost" className="w-full" onClick={() => setLimit(limit + PAGE)} data-testid="mat-load-more">
            Mostrar más ({items.length - limit} restantes)</Button>}
        </div>
      )}
      <ProposalFromSignalDialog companyId={companyId} signal={proposing} onClose={() => setProposing(null)} />
      {isAdmin && <KeyKpisDialog companyId={companyId} open={keysOpen} onClose={() => setKeysOpen(false)} />}
    </div>
  );
};

export const MaterialityBadge = ({ companyId }) => {
  const { data } = useQuery({
    queryKey: ['materiality', 'summary', companyId], enabled: !!companyId, refetchInterval: 60000,
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/materiality/summary`)).data,
  });
  const n = (data?.alerts?.critica || 0) + (data?.alerts?.alta || 0);
  if (!n) return null;
  return <span className="ml-auto rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" data-testid="mat-badge">{n}</span>;
};
